import { ClinicHours, NextOpenDay, TimeRange } from '../types';
import dateFormat from 'date-fns/format';
import add from 'date-fns/add';
import subMinutes from 'date-fns/subMinutes';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';

export const formatDay = (day: Date): string => {
    return dateFormat(day, 'eeee');
};

export const formatReadableDate = (day: Date): string => {
    return day instanceof Date && !isNaN(day.getTime())
        ? dateFormat(day, 'MMM dd, yyyy')
        : '';
};

export const formatReadableTime = (day: Date): string => {
    return formatTime(dateFormat(day, 'HH:mm:ss'));
};

export const formatShortDay = (day: string): string => {
    return day.substring(0, 3);
};

export const formatTime = (time: string): string => {
    const timeMinute = time.substring(2, 5);
    const timeHour = parseInt(time.substring(0, 2));
    let formattedTime;
    if (timeHour === 12) {
        formattedTime = timeHour + timeMinute + ' PM';
    } else if (timeHour === 0) {
        formattedTime = '12:' + timeMinute + ' AM';
    } else if (timeHour > 12) {
        const timeFormattedHour = timeHour % 12;
        formattedTime = timeFormattedHour + timeMinute + ' PM';
    } else {
        formattedTime = timeHour + timeMinute + ' AM';
    }
    return formattedTime;
};

export const getNextOpenDay = (
    openDays: ClinicHours[] | undefined
): NextOpenDay => {
    // this specifies how many days of the week to continue looping through
    const week: string[] = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];

    const times = {
        openHours: {
            isTomorrow: true,
            dayOfTheWeek: '',
            openTime: '',
            closeTime: '',
        },
    };

    for (const [index] of Array.from(week.entries())) {
        const nextOpenDay = formatDay(add(new Date(), { days: index + 1 }));
        const returnedDay = openDays?.find(
            (day: ClinicHours) => day.day === nextOpenDay
        );

        if (returnedDay) {
            times.openHours = {
                ...times.openHours,
                ...returnedDay.openHours,
                dayOfTheWeek: returnedDay.day,
                isTomorrow: true,
            };
            break;
        }
    }

    return times;
};

export const formatDayTimeRange = (clinicHours: ClinicHours[]): TimeRange[] => {
    const timeRangeList: TimeRange[] = [];

    clinicHours.forEach((clinicHourDay) => {
        const timeRangeDayCandidate: TimeRange = {
            timeRange: {
                startTime: formatTime(clinicHourDay.openHours.startTime),
                endTime: formatTime(clinicHourDay.openHours.endTime),
            },
            day: {
                startDay: formatShortDay(clinicHourDay.day),
                endDay: '',
            },
        };
        if (
            timeRangeList.length === 0 ||
            (timeRangeList[timeRangeList.length - 1] &&
                timeRangeList[timeRangeList.length - 1].timeRange.startTime !==
                    timeRangeDayCandidate.timeRange.startTime &&
                timeRangeList[timeRangeList.length - 1].timeRange.endTime !==
                    timeRangeDayCandidate.timeRange.endTime)
        ) {
            timeRangeList.push(timeRangeDayCandidate);
        } else {
            timeRangeList[timeRangeList.length - 1].day.endDay =
                timeRangeDayCandidate.day.startDay;
        }
    });

    return timeRangeList;
};

export const parseTime = (time: string): number[] => {
    const AM = !!time.match('AM');
    const PM = !!time.match('PM');

    const newTime = time.split(':');

    let hour = parseInt(newTime[0].trim());

    if (!AM && !PM) {
        return [hour, parseInt(newTime[1])];
    }

    const min = parseInt(newTime[1].slice(0, -2).trim());

    if (PM && hour !== 12) {
        hour = 12 + parseInt(newTime[0], 10);
    }

    return [hour, min];
};

export const parseDate = (date: string): string[] => {
    const dashDateRegex = /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
    if (date.match(dashDateRegex)) {
        const splitDate = date.split('-');

        return [splitDate[1], splitDate[2], splitDate[0]];
    }
    return date.split('/');
};

// Parses date of format MM-DD-YYYY into array [MM, DD, YYYY]
// Returns an empty array if an invalid format was provided for the date
export const parseMonthDayYear = (date: string): string[] => {
    const dateReg = /(\d+)\/(\d+)\/(\d+)/;
    const dateParts = date.match(dateReg);
    return dateParts?.slice(1) || [];
};

export const sortByDate = (dateA: Date, dateB: Date): number => {
    return isBefore(dateA, dateB) ? -1 : isAfter(dateA, dateB) ? 1 : 0;
};

export const converttoUTCDate = (date: string, time: string) => {
    const dateString = parseDate(date);
    const convertedTime = parseTime(time);

    return new Date(
        parseInt(dateString[2]), // year
        parseInt(dateString[0]) - 1, // month
        parseInt(dateString[1]), // day
        convertedTime[0], // hour
        convertedTime[1] // minute
    );
};

export const subtractMinutesFromDate = (date: string, time: string): string => {
    const result = subMinutes(new Date(converttoUTCDate(date, time)), 15);

    return dateFormat(result, 'p');
};

export const formatForAppointmentDisplay = (date: string): string[] => {
    // 8am is a dummy time since all we care about is formatting the date but want to use converttoUTCDate
    const result = converttoUTCDate(date, '08:00 AM');

    const convertedDate = dateFormat(result, 'MMM/dd/yyyy');

    return convertedDate.split('/');
};

export const formatTimeAndDate = (
    date: string,
    time: string,
    format: string
): string => {
    const result = new Date(converttoUTCDate(date, time));

    return dateFormat(result, format);
};

export const checkIfToday = (dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
        today.getFullYear() === date.getFullYear() &&
        today.getMonth() === date.getMonth() &&
        today.getDate() === date.getDate()
    );
};

export const checkIfTomorrow = (dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (
        tomorrow.getFullYear() === date.getFullYear() &&
        tomorrow.getMonth() === date.getMonth() &&
        tomorrow.getDate() === date.getDate()
    );
};
